/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import buildSidebarThreadCollectionsFromThreadItems from '@/lib/threads/buildSidebarThreadCollectionsFromThreadItems.js';
import buildChronologicalThreadSection from '@/lib/threads/buildChronologicalThreadSection.js';
import buildAutomationActiveByThreadId from '@/lib/threads/buildAutomationActiveByThreadId.js';
import buildThreadItems from '@/lib/threads/buildThreadItems.js';
import buildThreadsByProject from '@/lib/threads/buildThreadsByProject.js';
import buildThreadsByType from '@/lib/threads/buildThreadsByType.js';
import filterThreadsForMainScope from '@/lib/threads/filterThreadsForMainScope.js';
import getPendingInteractionsFromActivity from '@/lib/threadInteractions/getPendingInteractionsFromActivity.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@/redux/selectors/pieveSelectors/constants.js';
import { companyThreads, hasCanonicalThreadsArray, threadContentMap, threadMetaById, threads, workflows } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { activeProjectId, allowProjectFolders, projectMetaById, sidebarThreadShowModePreference, sidebarThreadSortByPreference } from '@/redux/selectors/pieveSelectors/sidebarProjectSelectors.js';
import { userId as authUserId } from '@/redux/selectors/userSelectors.js';

export const legacyThreadsByType = (state) => state?.pieve?.threadsByType || EMPTY_OBJECT;
export const legacyThreadsByProject = (state) => state?.pieve?.threadsByProject || EMPTY_OBJECT;
export const legacyThreadItems = (state) => state?.pieve?.sidebar?.threadItems || EMPTY_ARRAY;
export const legacyOnboardingThreadItems = (_state) => EMPTY_ARRAY;
export const legacyPinnedThreadItems = (state) => state?.pieve?.sidebar?.pinnedThreadItems || EMPTY_ARRAY;
export const legacyThreadSections = (state) => state?.pieve?.sidebar?.threadSections || EMPTY_ARRAY;
export const storedScheduleItems = (state) => state?.pieve?.scheduleItems || EMPTY_ARRAY;
export const storedTriggerItems = (state) => state?.pieve?.triggerItems || EMPTY_ARRAY;
export const mainScopeThreads = createSelector([threads], (threadItemsValue) => {
  return filterThreadsForMainScope(threadItemsValue);
});
export const mainSidebarThreads = createSelector([mainScopeThreads, companyThreads], (mainScopeThreadsValue, companyThreadsValue) => {
  return [
    ...mainScopeThreadsValue,
    ...companyThreadsValue.map((thread) => ({
      ...thread,
      projectId: 'project-home'
    }))
  ];
});

export const automationActiveByThreadId = createSelector([storedScheduleItems, storedTriggerItems, workflows], (scheduleItemsValue, triggerItemsValue, workflowItemsValue) => {
  return buildAutomationActiveByThreadId({
    scheduleItems: scheduleItemsValue,
    triggerItems: triggerItemsValue,
    workflowItems: workflowItemsValue
  });
});

export const pendingInteractionThreadIdSet = createSelector([threadContentMap, threadMetaById], (threadContentMapValue, threadMetaByIdValue) => {
  const threadIds = new Set();

  for (const [threadId, threadContentValue] of Object.entries(threadContentMapValue || EMPTY_OBJECT)) {
    const normalizedThreadId = String(threadId || '').trim();

    if (!normalizedThreadId) {
      continue;
    }

    const threadMetaValue = threadMetaByIdValue?.[normalizedThreadId] || EMPTY_OBJECT;
    const runState = threadContentValue?.runState || threadMetaValue?.runState || EMPTY_OBJECT;
    const pendingInteractions = getPendingInteractionsFromActivity({
      activity: threadContentValue?.activity,
      runState: runState?.state || 'idle',
      activeRunId: runState?.runId || null
    });

    if (pendingInteractions.length > 0) {
      threadIds.add(normalizedThreadId);
    }
  }

  return threadIds;
});

export const threadItems = createSelector([mainSidebarThreads, projectMetaById, automationActiveByThreadId, pendingInteractionThreadIdSet, sidebarThreadSortByPreference, authUserId, legacyThreadItems, hasCanonicalThreadsArray], (mainSidebarThreadsValue, projectMetaByIdValue, automationActiveByThreadIdValue, pendingInteractionThreadIdSetValue, sidebarThreadSortByPreferenceValue, authUserIdValue, legacyThreadItemsValue, currentHasCanonicalThreadsArray) => {
  if (!currentHasCanonicalThreadsArray) {
    return Array.isArray(legacyThreadItemsValue) ? legacyThreadItemsValue : EMPTY_ARRAY;
  }

  return buildThreadItems({
    threads: mainSidebarThreadsValue,
    projectMetaById: projectMetaByIdValue,
    automationActiveByThreadId: automationActiveByThreadIdValue,
    pendingInteractionThreadIdSet: pendingInteractionThreadIdSetValue,
    sortBy: sidebarThreadSortByPreferenceValue,
    currentUserId: authUserIdValue
  });
});

export const automationPinnedThreadIds = createSelector([threads], (threadMetaItems) => {
  const allowedThreadTypes = new Set(['schedule', 'schedule_run', 'trigger', 'trigger_run', 'workflow']);
  const ids = [];

  for (const threadMeta of Object.values(threadMetaItems || {})) {
    const threadId = String(threadMeta?.id || '').trim();
    const threadType = String(threadMeta?.threadType || threadMeta?.thread_type || '')
      .trim()
      .toLowerCase();
    const isPinned = Boolean(threadMeta?.pinned || threadMeta?.pinnedAt);

    if (!threadId || !isPinned || !allowedThreadTypes.has(threadType)) {
      continue;
    }

    ids.push(threadId);
  }

  return ids;
});

export const derivedSidebarThreadCollections = createSelector([threadItems, projectMetaById, activeProjectId, sidebarThreadSortByPreference, sidebarThreadShowModePreference], (threadItemsValue, projectMetaByIdValue, activeProjectIdValue, sidebarThreadSortByPreferenceValue, sidebarThreadShowModePreferenceValue) => {
  return buildSidebarThreadCollectionsFromThreadItems({
    threadItems: threadItemsValue,
    projectMetaById: projectMetaByIdValue,
    activeProjectId: activeProjectIdValue,
    sortBy: sidebarThreadSortByPreferenceValue,
    showMode: sidebarThreadShowModePreferenceValue
  });
});

export const onboardingThreadItems = createSelector([derivedSidebarThreadCollections, legacyOnboardingThreadItems, hasCanonicalThreadsArray], (derivedSidebarCollectionsValue, legacyOnboardingThreadItemsValue, currentHasCanonicalThreadsArray) => {
  if (!currentHasCanonicalThreadsArray) {
    return Array.isArray(legacyOnboardingThreadItemsValue) ? legacyOnboardingThreadItemsValue : EMPTY_ARRAY;
  }

  return derivedSidebarCollectionsValue.onboardingThreadItems;
});

export const pinnedThreadItems = createSelector([mainScopeThreads, derivedSidebarThreadCollections, legacyPinnedThreadItems, hasCanonicalThreadsArray], (mainScopeThreadsValue, derivedSidebarCollectionsValue, legacyPinnedThreadItemsValue, currentHasCanonicalThreadsArray) => {
  if (!currentHasCanonicalThreadsArray) {
    return Array.isArray(legacyPinnedThreadItemsValue) ? legacyPinnedThreadItemsValue : EMPTY_ARRAY;
  }

  return derivedSidebarCollectionsValue.pinnedThreadItems;
});

export const automationPinnedThreadIdSet = createSelector([pinnedThreadItems, automationPinnedThreadIds], (pinnedThreadItemsValue, automationPinnedThreadIdsValue) => {
  const ids = new Set();

  for (const item of pinnedThreadItemsValue || EMPTY_ARRAY) {
    const normalizedId = String(item?.id || '').trim();

    if (!normalizedId) {
      continue;
    }

    ids.add(normalizedId);
  }

  for (const threadId of automationPinnedThreadIdsValue || EMPTY_ARRAY) {
    const normalizedId = String(threadId || '').trim();

    if (!normalizedId) {
      continue;
    }

    ids.add(normalizedId);
  }

  return ids;
});

export const threadsByProject = createSelector([mainScopeThreads, legacyThreadsByProject, hasCanonicalThreadsArray], (mainScopeThreadsValue, legacyThreadsByProjectValue, currentHasCanonicalThreadsArray) => {
  if (!currentHasCanonicalThreadsArray) {
    return legacyThreadsByProjectValue;
  }

  return buildThreadsByProject({
    threads: mainScopeThreadsValue
  });
});

export const threadsByType = createSelector([threads, legacyThreadsByType, hasCanonicalThreadsArray], (threadItemsValue, legacyThreadsByTypeValue, currentHasCanonicalThreadsArray) => {
  if (currentHasCanonicalThreadsArray) {
    const derivedThreadsByType = buildThreadsByType({
      threads: threadItemsValue
    });

    return {
      schedules: derivedThreadsByType.schedules,
      triggers: derivedThreadsByType.triggers,
      workflows: derivedThreadsByType.workflows
    };
  }

  if (legacyThreadsByTypeValue && Object.keys(legacyThreadsByTypeValue).length > 0) {
    return legacyThreadsByTypeValue;
  }

  return {
    schedules: EMPTY_ARRAY,
    triggers: EMPTY_ARRAY,
    workflows: EMPTY_ARRAY
  };
});

export const scheduleThreads = (state) => threadsByType(state).schedules;
export const triggerThreads = (state) => threadsByType(state).triggers;
export const workflowThreads = (state) => threadsByType(state).workflows;

export const threadSections = createSelector([mainScopeThreads, derivedSidebarThreadCollections, legacyThreadSections, hasCanonicalThreadsArray], (mainScopeThreadsValue, derivedSidebarCollectionsValue, legacyThreadSectionsValue, currentHasCanonicalThreadsArray) => {
  if (!currentHasCanonicalThreadsArray) {
    return Array.isArray(legacyThreadSectionsValue) ? legacyThreadSectionsValue : EMPTY_ARRAY;
  }

  return derivedSidebarCollectionsValue.threadSections;
});

export const visibleThreadSections = createSelector([threadSections, allowProjectFolders, sidebarThreadSortByPreference], (threadSectionsValue, allowProjectFoldersValue, sidebarThreadSortByPreferenceValue) => {
  if (allowProjectFoldersValue || threadSectionsValue.length < 1) {
    return threadSectionsValue;
  }

  return [
    buildChronologicalThreadSection({
      sections: threadSectionsValue,
      sortBy: sidebarThreadSortByPreferenceValue
    })
  ];
});
